import { Dispatch, FC, SetStateAction } from 'react'
import {
	Control,
	Controller,
	FormState,
	UseFormGetValues,
	UseFormReset,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch
} from 'react-hook-form'

import { Button, Typography } from '@/components/ui'
import CustomSelectLanguage from '@/components/ui/select/CustomSelectLanguage'
import Spinner from '@/components/ui/spinner/Spinner'

import { STATUS, STEP_TYPE } from '@/constants/common'
import { IOption } from '@/types/selectOption.interface'
import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'
import {
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformLabel,
	TPlatformValue
} from '@/types/uploadForm.interface'

import ModalSelectPlatform from './ModalSelectPlatform'
import ModalSelectWay from './ModalSelectWay'
import ModalUploadForm from './ModalUploadForm'
import ModalWindowHeader from './ModalWindowHeader'

interface FormUploaderLayoutProps {
	modalRef: React.RefObject<HTMLDivElement>
	selectedWay: string
	setSelectedWay: (selectedWay: string) => void
	setIsModalOpen: (open: boolean) => void
	status?: string
	openFormModal: boolean
	setOpenFormModal: (open: boolean) => void
	isUploaded: boolean
	isUpload?: boolean
	isFormUploading: boolean
	handleNextClick: () => void
	platforms?: IPlatform[] | null
	isPlatformsLoading: boolean
	firstPlatform: IPlatform['value']
	setFirstPlatform: Dispatch<SetStateAction<IPlatform['value']>>
	uploaderFormControl: Control<IUploaderForm>
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	uploaderFormWatch: UseFormWatch<IUploaderForm>
	uploaderFormSetValue: UseFormSetValue<IUploaderForm>
	formState: FormState<IUploaderForm>
	uploaderFormReset: UseFormReset<IUploaderForm>
	trigger: UseFormTrigger<IUploaderForm>
	error?: boolean
	taskId: string
	setIsUploaded: (isUploaded: boolean) => void
	setIsFormUploading: (value: boolean) => void
	isSuccess?: boolean
	serverHasVideo: boolean
	firstPlatformLabel: TPlatformLabel
	languages: ILanguage[]
	isUploading?: boolean
	isTranscribing?: boolean
	onTranscribe: (value: string) => void
	onTranslate: (value: string) => void
	onUploadSrt: (value: File) => void
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang: Dispatch<SetStateAction<ILanguage | null>>
	isTranslating: boolean
	selectedPlatform: TPlatformValue | null
	setSelectedPlatform: (value: TPlatformValue | null) => void
	selectedPlatformList: ISelectedPlatform[]
	setSelectedPlatformList: Dispatch<SetStateAction<ISelectedPlatform[]>>
	availableSourceLanguages: IOption[]
	setAvailableSourceLanguages: Dispatch<SetStateAction<IOption[]>>
	refreshCurrentShotlist: () => Promise<IShotlist | null>
	currentShotlist: IShotlist | null
	pendingSrtFileName: string
	handleDeleteTranslation: () => Promise<void>
	onStopTranscription: () => Promise<void>
	handleAutoFill: () => Promise<void>
	effectiveAutoFillSourcePlatform: TPlatformValue | ''
	setPreferredAutoFillSourcePlatform: Dispatch<
		SetStateAction<TPlatformValue | ''>
	>
	isMP4?: boolean
	filename?: string
	control: Control<{ language: ILanguage }>
	onNextClick: (language: string) => void
}

export const FormUploaderLayout: FC<FormUploaderLayoutProps> = ({
	modalRef,
	selectedWay,
	setSelectedWay,
	setIsModalOpen,
	status,
	openFormModal,
	setOpenFormModal,
	isUploaded,
	isUpload,
	isFormUploading,
	handleNextClick,
	platforms,
	isPlatformsLoading,
	firstPlatform,
	setFirstPlatform,
	uploaderFormControl,
	uploaderFormGetValues,
	uploaderFormWatch,
	uploaderFormSetValue,
	formState,
	uploaderFormReset,
	trigger,
	error,
	taskId,
	setIsUploaded,
	setIsFormUploading,
	isSuccess,
	serverHasVideo,
	firstPlatformLabel,
	languages,
	isUploading,
	isTranscribing,
	onTranscribe,
	onTranslate,
	onUploadSrt,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	isTranslating,
	selectedPlatform,
	setSelectedPlatform,
	selectedPlatformList,
	setSelectedPlatformList,
	availableSourceLanguages,
	setAvailableSourceLanguages,
	refreshCurrentShotlist,
	currentShotlist,
	pendingSrtFileName,
	handleDeleteTranslation,
	onStopTranscription,
	handleAutoFill,
	effectiveAutoFillSourcePlatform,
	setPreferredAutoFillSourcePlatform,
	isMP4,
	filename,
	control,
	onNextClick
}) => {
	return (
		<div
			ref={modalRef}
			className={`fixed z-[50] h-full top-0 bg-white py-6 px-[40px] ${
				isUpload || openFormModal ? 'w-3/4 left-1/4' : 'w-full left-0'
			}`}
		>
			{isFormUploading && (
				<>
					<div className='w-full h-full absolute z-20 bg-white opacity-50' />
					<Spinner className='absolute !z-30' />
				</>
			)}

			<ModalWindowHeader
				taskId={taskId}
				selectedWay={selectedWay}
				setSelectedWay={setSelectedWay}
				setIsModalOpen={setIsModalOpen}
				hideBackButton={openFormModal}
				status={status}
				openFormModal={openFormModal}
				isUploaded={isUploaded}
			/>

			{/* Upload */}
			{/* Step 1 */}

			{selectedWay === STEP_TYPE.UPLOAD && !openFormModal && (
				<ModalSelectPlatform
					setOpenFormModal={setOpenFormModal}
					handleUpload={() => onNextClick('')}
					platformList={platforms}
					isPlatformLoading={isPlatformsLoading}
					firstPlatform={firstPlatform}
					setFirstPlatform={setFirstPlatform}
				/>
			)}

			{openFormModal && (
				<ModalUploadForm
					uploaderFormControl={uploaderFormControl}
					uploaderFormGetValues={uploaderFormGetValues}
					uploaderFormWatch={uploaderFormWatch}
					uploaderFormSetValue={uploaderFormSetValue}
					formState={formState}
					uploaderFormReset={uploaderFormReset}
					trigger={trigger}
					error={error}
					taskId={taskId}
					isUploaded={isUploaded}
					setIsUploaded={setIsUploaded}
					setIsFormUploading={setIsFormUploading}
					isSuccess={isSuccess}
					hasUploadedVideo={serverHasVideo}
					firstPlatformLabel={firstPlatformLabel}
					platformList={platforms}
					languages={languages}
					isUploading={isUploading}
					isTranscribing={isTranscribing}
					handleTranscribe={onTranscribe}
					handleTranslate={onTranslate}
					handleUploadSrt={onUploadSrt}
					selectedTranscribingLang={selectedTranscribingLang}
					setSelectedTranscribingLang={setSelectedTranscribingLang}
					isTranslating={isTranslating}
					selectedPlatform={selectedPlatform}
					setSelectedPlatform={setSelectedPlatform}
					selectedPlatformList={selectedPlatformList}
					setSelectedPlatformList={setSelectedPlatformList}
					availableSourceLanguages={availableSourceLanguages}
					setAvailableSourceLanguages={setAvailableSourceLanguages}
					refetchShotlist={refreshCurrentShotlist}
					shotlist={currentShotlist}
					pendingSrtFileName={pendingSrtFileName}
					handleDeleteTranslation={handleDeleteTranslation}
					onStopTranscription={onStopTranscription}
					handleAutoFill={handleAutoFill}
					sourceAutoFillLang={effectiveAutoFillSourcePlatform}
					setSourceAutoFillLang={setPreferredAutoFillSourcePlatform}
				/>
			)}

			{/* Transcribe */}
			{/* Step 1 */}
			{!selectedWay && (
				<ModalSelectWay
					setSelectedWay={setSelectedWay}
					isMP4={isMP4}
					filename={filename}
				/>
			)}

			{/* Step 2 */}
			{selectedWay === STEP_TYPE.TRANSCRIBE && status !== STATUS.FINISHED && (
				<div className='flex justify-center'>
					<div>
						<Typography
							variant='text-xlarge'
							className='!leading-[40px] !font-[700] mb-6'
						>
							Select source language
						</Typography>
						<div className='flex flex-col gap-4'>
							<Controller
								name='language'
								control={control}
								render={({ field }) => (
									<CustomSelectLanguage
										{...field}
										value={field.value}
										onChange={field.onChange}
										options={languages}
										placeholder='English'
									/>
								)}
							/>

							<Button
								onClick={handleNextClick}
								className='!z-0 !w-[525px] bg-black rounded-[8px] px-4 py-[20px] hover:bg-gray-600'
								classNameText='text-white text-[16px] font-semibold'
							>
								Start uploading and transcribing
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
